import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * `?open=<id>` — a list page asked to arrive with one row already open.
 *
 * The Documents screen lists invoices, recaps and credit notes next to the orders, and a click
 * there used to land on the plain list page with nothing selected: the operator had found the
 * document and then had to find it again. The list pages keep their own detail panel; this
 * just selects the requested row the moment the rows are in, then drops the parameter so a
 * refresh or a Back does not re-open it.
 *
 * Silent when the id is not in the list (out of the caller's scope, or gone): the page is still
 * the right place to be, and an empty list says more than an error would.
 */
export function useOpenParam<T extends { id: string }>(
  rows: T[],
  loading: boolean,
  open: (row: T) => void
): void {
  const [params, setParams] = useSearchParams();
  const wanted = params.get('open');
  useEffect(() => {
    if (!wanted || loading) return;
    const row = rows.find((r) => r.id === wanted);
    if (row) open(row);
    const next = new URLSearchParams(params);
    next.delete('open');
    setParams(next, { replace: true });
    // `open` is a state setter or a stable callback; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, loading, rows]);
}
